import { RootState } from "./../../../../../store";
import { useState } from "react";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ErrorNotification from "../../../../../common/components/notification/ErrorNotification";

import ConfirmModal from "../../../../../common/components/modal/ConfirmModal";
import MESSAGE, {
  LABEL_MESSAGE,
  NOTIFICATION_TITLE,
} from "../../../../../common/constants/MESSAGE";
import moment from "moment";
import SuccessNotification from "../../../../../common/components/notification/SuccessNotification";
import helpers from "../../../../../common/helpers/common";
import {
  CertificateMemberEntity,
  DetailMemberEntity,
  MemberUpdateEntity,
} from "../../../../OM/OM006/entity/Entity";
import { setLoading } from "../../../../../common/slice/CommonSlice";
import {
  CertificateEntity,
  MemberCertificateEntity,
} from "../../entity/Entity";
import { setRefreshOM003 } from "../slice/Slice";
import { setAuth } from "../../../../../common/slice/AuthSlice";
import { OM003UseCase } from "../../usecase/ServiceImpl";
import ConfirmModalAsync from "../../../../../common/components/modal/ConfirmModalAsync";
import { useNavigate } from "react-router-dom";
import {
  setIsVisibleOM006,
  setMemberIdOM006,
} from "../../../OM006/presenter/slice/Slice";

export const CERT_LINE_DEFAULT = {
  isDone: false,
  lines: {},
};

const DEFAULT_STATE = {
  certLines: CERT_LINE_DEFAULT,
  certType: [],
  memberName: "",
  detailMember: null,
  isDirty: false,
  isEditing: false,
};

const Handler = (service: OM003UseCase) => {
  // LIB FUNCTION
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // STATE
  const [fileData, setFileData] = useState<any>();
  const [isDirty, setIsDirty] = useState(DEFAULT_STATE.isDirty);
  const [certType, setCertType] = useState<CertificateEntity[]>(
    DEFAULT_STATE.certType
  );
  const [detailMember, setDetailMember] = useState<DetailMemberEntity | null>(
    DEFAULT_STATE.detailMember
  );
  const [certLines, setCertLines] = useState<any>(DEFAULT_STATE.certLines);
  const [memberName, setMemberName] = useState<string>(
    DEFAULT_STATE.memberName
  );
  const [isEditing, setIsEditing] = useState<boolean>(DEFAULT_STATE.isEditing);

  // FUNCTION
  const getAvatarBinary = (value: any) => {
    setFileData(value);
  };

  const onNameChange = (e: any) => {
    setMemberName(e.target.value);
  };

  const setInitialForm = (detailMember: DetailMemberEntity | null) => {
    if (!detailMember) return;
    // default form item value
    const defaultValue: any = {
      name: detailMember.fullName,
      id: detailMember.username,
      email: detailMember.emailAddress,
      phoneNumber: helpers.toPhoneNumberFormat(detailMember.phoneNumber),
      address: detailMember.address,
    };

    // group
    if (detailMember?.group?.id)
      defaultValue.branchId = detailMember.group.id + "";

    // role
    if (detailMember?.role?.id) {
      defaultValue.roleId = detailMember.role.id + "";
    }

    // CCUSID
    if (detailMember.ccusId) {
      defaultValue.CCUSID = helpers.convertToCCUSFormat(detailMember.ccusId);
      if (detailMember.ccusExpiredDate)
        defaultValue.expirationDate = moment(detailMember.ccusExpiredDate);
    }

    // certifactes
    (detailMember?.certificates ?? []).forEach(
      (element: CertificateMemberEntity) => {
        defaultValue[`cert${element.category.id}${element.id}`] = {
          name: element.name,
          issueDate: element?.issueDate ? moment(element.issueDate) : null,
          registrationCode: element.registrationCode,
          issuer: element.issuer,
          expireDate: element?.expireDate ? moment(element.expireDate) : null,
        };
      }
    );

    form.setFieldsValue(defaultValue);
  };

  const setCertificateSection = (detailMember: DetailMemberEntity | null) => {
    // certifactes
    const certTypes: any = {};
    (detailMember?.certificates ?? []).forEach(
      (element: CertificateMemberEntity) => {
        if (certTypes[`cert${element.category.id}`])
          certTypes[`cert${element.category.id}`].push(element);
        else {
          certTypes[`cert${element.category.id}`] = [element];
        }
      }
    );
    setCertLines({
      isDone: true,
      lines: certTypes,
    });

    setCertLines((prevState: any) => ({ ...prevState, lines: certTypes }));

    setCertLines((prevState: any) => ({
      ...prevState,
      isDone: true,
    }));
  };

  const getMemberDetail = async (memberId: number) => {
    try {
      dispatch(setLoading(true));
      const detailMemberData = await service.getDetailMember({
        id: memberId,
      });
      setMemberName(detailMemberData?.fullName ?? "");
      setCertificateSection(detailMemberData);
      setDetailMember(detailMemberData);
    } catch (error: any) {
      ErrorNotification(error?.message ?? NOTIFICATION_TITLE.ERROR);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getCertificateType = async () => {
    try {
      dispatch(setLoading(true));
      const certificateTypes = await service.getCertificateType();
      setCertType(certificateTypes);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getRoleList = async (params: any) => {
    return await service.getRoleList(params);
  };

  const getBranchList = async (params: any) => {
    return await service.getBranchList(params);
  };

  const updateMember = async (formData: any) => {
    try {
      dispatch(setLoading(true));

      if (!detailMember?.id) return;
      // avatar
      const avatarFormValueArr = formData?.avatar ?? [];
      const avatarFileName =
        avatarFormValueArr.length === 0
          ? undefined
          : avatarFormValueArr[avatarFormValueArr.length - 1];

      // certificates
      const memberCertificates: MemberCertificateEntity[] = [];
      const formProperties = Object.keys(formData);
      formProperties.forEach((element: string) => {
        if (element.startsWith("cert") && formData[`${element}`].name) {
          memberCertificates.push({
            categoryId: parseInt(formData[`${element}`].certTypeId),
            expireDate: formData[`${element}`].expireDate
              ? formData[`${element}`].expireDate.toISOString()
              : "",
            id: parseInt(formData[`${element}`].certId),
            issueDate: formData[`${element}`].issueDate
              ? formData[`${element}`].issueDate.toISOString()
              : "",
            issuer: formData[`${element}`].issuer ?? "",
            registrationCode: formData[`${element}`].registrationCode ?? "",
            name: formData[`${element}`].name ?? "",
          });
        }
      });

      const data: MemberUpdateEntity = {
        avatar: avatarFileName?.name ?? undefined,
        id: detailMember?.id ?? 0,
        certificates: memberCertificates,
        email: formData.email,
        fullName: formData.name,
        username: formData.id,
        phoneNumber: formData.phoneNumber.replaceAll("-", ""),
        roleId: formData.roleId
          ? Number(formData.roleId)
          : detailMember?.role?.id,
        address: formData?.address ?? "",
        ccusId: formData.CCUSID || "",
        ccusExpiredDate: formData?.expirationDate?.toISOString() || "",
      };
      if (formData.branchId) data.groupId = Number(formData.branchId);

      const response = await service.updateMember(data);
      if (formData.avatar) {
        // Nếu người dùng nhập avatar
        const preSignResult = await service.getPresignAvatarUpload({
          avatar: avatarFileName.name,
          userId: detailMember.id,
        });
        await service.uploadAvatar(
          fileData,
          preSignResult.results,
          fileData?.type
        );
        await service.updateMemberAvatar({
          avatar: fileData?.name ?? "",
          userId: detailMember.id,
        });
      } else if (!formData.hiddenAvatar) {
        await service.updateMemberAvatar({
          avatar: "",
          userId: detailMember.id,
        });
      }
      resetAllState();
      dispatch(setRefreshOM003());
      SuccessNotification(response?.message ?? NOTIFICATION_TITLE.SUCCESS);
    } catch (error: any) {
      ErrorNotification(error?.message ?? NOTIFICATION_TITLE.ERROR);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const resetAllState = () => {
    form.resetFields();
    setIsDirty(DEFAULT_STATE.isDirty);
    setIsEditing(DEFAULT_STATE.isEditing);
    setFileData(undefined);
    setDetailMember(DEFAULT_STATE.detailMember);
    setCertLines(DEFAULT_STATE.certLines);
    setMemberName(DEFAULT_STATE.memberName);
  };

  const resetState = () => {
    form.resetFields();
    setIsDirty(DEFAULT_STATE.isDirty);
    setIsEditing(DEFAULT_STATE.isEditing);
  };

  const onCancel = () => {
    if (!isDirty) {
      resetState();
      return;
    }
    ConfirmModal({
      onOk: () => resetState(),
      className: "confirm__modal",
      title: MESSAGE.MESSAGE_020,
      description: MESSAGE.MESSAGE_022_1,
      extraDescription: MESSAGE.MESSAGE_022_2,
      canceText: LABEL_MESSAGE.CANCEL_MODAL,
      okText: LABEL_MESSAGE.OK_MODAL,
      isCenterWithoutMenu: true,
    });
  };

  const backToListMemberPage = () => {
    if (!isDirty) {
      navigate("/app-om002");
      return;
    }
    ConfirmModal({
      onOk: () => navigate("/app-om002"),
      className: "confirm__modal",
      title: MESSAGE.MESSAGE_020,
      description: MESSAGE.MESSAGE_022_1,
      extraDescription: MESSAGE.MESSAGE_022_2,
      canceText: LABEL_MESSAGE.CANCEL_MODAL,
      okText: LABEL_MESSAGE.OK_MODAL,
      isCenterWithoutMenu: true,
    });
  };

  const checkDirty = (): void => {
    const formValue = form.getFieldsValue();
    const formProps = Object.keys(formValue);
    for (let i = 0; i < formProps.length; i++) {
      if (form.isFieldTouched(formProps[i])) {
        return setIsDirty(true);
      }
    }
    setIsDirty(false);
  };
  const changeEditStatus = (value: boolean) => {
    if (value) setInitialForm(detailMember);

    setIsEditing(value);
  };
  const onExpirationDateChange = (
    value: moment.Moment | null,
    dateString: string
  ) => {
    const ccusid = form.getFieldValue("CCUSID");
    if (!ccusid && !value)
      return form.setFields([
        {
          name: "CCUSID",
          errors: [],
        },
      ]);

    if (!ccusid)
      return form.setFields([
        {
          name: "CCUSID",
          errors: [MESSAGE.MESSAGE_LESS_THAN_14],
        },
      ]);
  };

  const onClickMenu = (callback: (value: any) => void, value: any) => {
    if (isEditing && isDirty) {
      ConfirmModal({
        onOk: () => {
          callback(value);
        },
        onCancel: () => {},
        title: MESSAGE.MESSAGE_020,
        description: MESSAGE.MESSAGE_021,
        canceText: LABEL_MESSAGE.CANCEL_MODAL,
        okText: LABEL_MESSAGE.OK_MODAL,
      });
    } else {
      callback(value);
    }
  };

  const removeAvatar = () => {
    form.resetFields(["avatar"]);
  };

  //params: id, workspaceId
  const deleteMember = async (id: any) => {
    ConfirmModalAsync({
      onOk: async () => {
        dispatch(setLoading(true));
        try {
          const params = {
            listId: [id],
          };
          const response = await service.deleteMember(params);
          SuccessNotification(response?.message ?? NOTIFICATION_TITLE.SUCCESS);
          navigate("/app-om002");
        } catch (error: any) {
          ErrorNotification(error?.message ?? NOTIFICATION_TITLE.ERROR);
        } finally {
          dispatch(setLoading(false));
        }
      },
      onCancel: () => {},
      className: "confirm__modal confirm__modal-confirm",
      title: MESSAGE.MESSAGE_DELETE_MANY_TIT,
      description: MESSAGE.MESSAGE_DELETE_MANY_DES_1,
      description2: MESSAGE.MESSAGE_DELETE_MANY_DES_2,
      canceText: LABEL_MESSAGE.CANCEL_MODAL,
      okText: LABEL_MESSAGE.OK_DELETE,
    });
  };

  const getGroups = async () => {
    dispatch(setLoading(true));
    try {
      const response = await service.getGroup();
      if (response?.results?.groups?.length > 0) {
        return {
          results: response?.results?.groups,
          pagination: {
            count: response?.results?.groups?.length,
          },
        };
      }
      return {
        results: response?.results?.groups,
        pagination: {
          count: response?.results?.groups?.length,
        },
      };
    } catch (error: any) {
      ErrorNotification(error?.message ?? NOTIFICATION_TITLE.ERROR);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const openModalEdit = (id: number) => {
    dispatch(setMemberIdOM006(id));
    dispatch(setIsVisibleOM006(true));
  };

  return {
    getBranchList,
    getRoleList,
    updateMember,
    onCancel,
    checkDirty,
    getCertificateType,
    getMemberDetail,
    getAvatarBinary,
    onNameChange,
    onExpirationDateChange,
    changeEditStatus,
    onClickMenu,
    removeAvatar,
    deleteMember,
    backToListMemberPage,
    getGroups,
    openModalEdit,

    isEditing,
    memberName,
    detailMember,
    form,
    isDirty,
    certType,
    certLines,
  };
};

export default Handler;
